// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState, useCallback } from 'react';
import * as yup from 'yup';
import type { StepComponentProps } from '../../../types/wizard';
import { FileUploadField } from '../shared/FileUploadField';
import { Dropdown, TextInput, TextArea } from '@loa/ui-components';
import { toRawNumber, formatCurrencyDisplay, formatEuroBlur } from './currencyHelpers';

const YES_NO_OPTIONS = [
  { value: 'Yes', text: 'Yes' },
  { value: 'No', text: 'No' },
];

const employmentSchema = yup.object({
  employerName: yup.string().trim().required('Employer name is required'),
  dateJoinedScheme: yup.string().required('Date joined scheme is required'),
  firstDateOfEmployment: yup.string().required('First date of employment is required'),
  schemePolicyNra: yup.string().trim().required('Scheme/policy NRA is required'),
  dateOfLeavingService: yup.string().optional(),
  salary: yup.string().trim().optional(),
  severancePackageReceived: yup.string().required('This field is required'),
  severanceWaivedLumpSum: yup.string().required('This field is required'),
});

function validateField(fieldName: string, data: Record<string, unknown>): string | undefined {
  try {
    employmentSchema.validateSyncAt(fieldName, data);
    return undefined;
  } catch (err) {
    if (err instanceof yup.ValidationError) return err.message;
    return undefined;
  }
}

type StepEmploymentDetailsProps = Readonly<StepComponentProps & {
  showSeveranceSubSection?: boolean;
}>;

export function StepEmploymentDetails({
  data,
  onFieldChange,
  fieldErrors,
  onClearFieldError,
  pendingFiles,
  onAddFile,
  onRemoveFile,
  showSeveranceSubSection = false,
}: StepEmploymentDetailsProps) {
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
  const handleBlur = useCallback((field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = validateField(field, data);
    setInlineErrors((prev) => {
      if (error) return { ...prev, [field]: error };
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, [data]);

  const getError = (field: string): string | undefined => {
    return (touched[field] ? inlineErrors[field] : undefined) || fieldErrors[field];
  };

  const handleCurrencyChange = useCallback((field: string, inputValue: string) => {
    const raw = toRawNumber(inputValue);
    if (raw && !/^\d*\.?\d*$/.test(raw)) return;
    onFieldChange(field, raw);
    onClearFieldError(field);
  }, [onFieldChange, onClearFieldError]);

  const handleCurrencyBlur = useCallback((field: string) => {
    const raw = (data[field] as string) || '';
    if (raw) {
      onFieldChange(field, formatEuroBlur(raw));
    }
    handleBlur(field);
  }, [data, onFieldChange, handleBlur]);

  const getCurrencyDisplay = (field: string): string => {
    const raw = (data[field] as string) || '';
    return raw ? formatCurrencyDisplay(raw) : '';
  };

  // Date picker SVG icon (calendar)
  const calendarIcon = (maskId: string) => (
    <svg className="absolute right-[16px] top-1/2 -translate-y-1/2 pointer-events-none" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id={maskId} style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
        <path d="M16 0H0V16H16V0Z" fill="white"/>
      </mask>
      <g mask={`url(#${maskId})`}>
        <path d="M5.3335 3.83333C5.06016 3.83333 4.8335 3.60666 4.8335 3.33333V1.33333C4.8335 1.05999 5.06016 0.833328 5.3335 0.833328C5.60683 0.833328 5.8335 1.05999 5.8335 1.33333V3.33333C5.8335 3.60666 5.60683 3.83333 5.3335 3.83333Z" fill="#141414"/>
        <path d="M10.6665 3.83333C10.3932 3.83333 10.1665 3.60666 10.1665 3.33333V1.33333C10.1665 1.05999 10.3932 0.833328 10.6665 0.833328C10.9398 0.833328 11.1665 1.05999 11.1665 1.33333V3.33333C11.1665 3.60666 10.9398 3.83333 10.6665 3.83333Z" fill="#141414"/>
        <path d="M13.6668 6.55989H2.3335C2.06016 6.55989 1.8335 6.33322 1.8335 6.05989C1.8335 5.78656 2.06016 5.55989 2.3335 5.55989H13.6668C13.9402 5.55989 14.1668 5.78656 14.1668 6.05989C14.1668 6.33322 13.9402 6.55989 13.6668 6.55989Z" fill="#141414"/>
        <path d="M10.6667 15.1667H5.33333C2.9 15.1667 1.5 13.7667 1.5 11.3333V5.66666C1.5 3.23333 2.9 1.83333 5.33333 1.83333H10.6667C13.1 1.83333 14.5 3.23333 14.5 5.66666V11.3333C14.5 13.7667 13.1 15.1667 10.6667 15.1667ZM5.33333 2.83333C3.42667 2.83333 2.5 3.75999 2.5 5.66666V11.3333C2.5 13.24 3.42667 14.1667 5.33333 14.1667H10.6667C12.5733 14.1667 13.5 13.24 13.5 11.3333V5.66666C13.5 3.75999 12.5733 2.83333 10.6667 2.83333H5.33333Z" fill="#141414"/>
      </g>
    </svg>
  );

  const dateInputClass = (field: string) =>
    `w-full h-[48px] px-md pr-2xl rounded-lg border ${getError(field) ? 'border-[#E7000B]' : 'border-grey-04'} bg-white ${(data[field] as string) ? 'text-[#333]' : 'text-[#B6B6B6]'} font-sans text-body-02 font-normal leading-normal outline-none focus:border-purple-primary placeholder:text-[#B6B6B6] placeholder:font-sans placeholder:text-body-02 placeholder:font-normal [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-[48px] [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer`;


  return (
    <div className="flex flex-col gap-lg">
      <div className="text-black font-sans text-h5 font-normal leading-normal not-italic">
        Employment details
      </div>

      {/* Row 1: Employer Name* | Date joined scheme* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <TextInput
            label="Employer name"
            required
            value={(data.employerName as string) || ''}
            onChange={(e) => {
              onFieldChange('employerName', e.target.value);
              onClearFieldError('employerName');
            }}
            onBlur={() => handleBlur('employerName')}
            error={getError('employerName')}
            fluid
          />
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date joined scheme <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={(data.dateJoinedScheme as string) || ''}
              onFocus={(e) => { e.currentTarget.type = 'date'; }}
              onBlur={(e) => {
                if (!e.currentTarget.value) e.currentTarget.type = 'text';
                handleBlur('dateJoinedScheme');
              }}
              onChange={(e) => {
                onFieldChange('dateJoinedScheme', e.target.value);
                onClearFieldError('dateJoinedScheme');
              }}
              className={dateInputClass('dateJoinedScheme')}
            />
            {calendarIcon('mask_djs')}
          </div>
          {getError('dateJoinedScheme') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('dateJoinedScheme')}</div>
          )}
        </div>
      </div>

      {/* Row 2: First Date of Employment* | Scheme/policy NRA* */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            First date of employment <span className="text-[#E7000B]">*</span>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={(data.firstDateOfEmployment as string) || ''}
              onFocus={(e) => { e.currentTarget.type = 'date'; }}
              onBlur={(e) => {
                if (!e.currentTarget.value) e.currentTarget.type = 'text';
                handleBlur('firstDateOfEmployment');
              }}
              onChange={(e) => {
                onFieldChange('firstDateOfEmployment', e.target.value);
                onClearFieldError('firstDateOfEmployment');
              }}
              className={dateInputClass('firstDateOfEmployment')}
            />
            {calendarIcon('mask_fde')}
          </div>
          {getError('firstDateOfEmployment') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('firstDateOfEmployment')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <TextInput
            label="Scheme/policy NRA"
            required
            value={(data.schemePolicyNra as string) || ''}
            onChange={(e) => {
              onFieldChange('schemePolicyNra', e.target.value);
              onClearFieldError('schemePolicyNra');
            }}
            onBlur={() => handleBlur('schemePolicyNra')}
            error={getError('schemePolicyNra')}
            fluid
          />
        </div>
      </div>

      {/* Row 3: Date of Leaving Service | Salary */}
      <div className="flex flex-row gap-lg">
        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Date of leaving service
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="DD/MM/YYYY"
              value={(data.dateOfLeavingService as string) || ''}
              onFocus={(e) => { e.currentTarget.type = 'date'; }}
              onBlur={(e) => {
                if (!e.currentTarget.value) e.currentTarget.type = 'text';
                handleBlur('dateOfLeavingService');
              }}
              onChange={(e) => {
                onFieldChange('dateOfLeavingService', e.target.value);
                onClearFieldError('dateOfLeavingService');
              }}
              className={dateInputClass('dateOfLeavingService')}
            />
            {calendarIcon('mask_dls')}
          </div>
          {getError('dateOfLeavingService') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('dateOfLeavingService')}</div>
          )}
        </div>

        <div className="flex flex-col gap-sm flex-1">
          <div className="text-black font-sans text-body-02 font-medium leading-normal not-italic">
            Salary as at date of leaving service
          </div>
          <div className={`flex items-center w-full h-[48px] px-md rounded-lg border ${getError('salary') ? 'border-[#E7000B]' : 'border-grey-04'} bg-white focus-within:border-purple-primary`}>
            <span className="text-black font-sans text-body-02 font-normal leading-normal select-none" style={{ marginRight: '8px' }}>€</span>
            <input
              type="text"
              inputMode="decimal"
              value={getCurrencyDisplay('salary')}
              onChange={(e) => handleCurrencyChange('salary', e.target.value)}
              onBlur={() => handleCurrencyBlur('salary')}
              className="flex-1 h-full bg-transparent text-[#333] font-sans text-body-02 font-normal leading-normal outline-none"
            />
          </div>
          {getError('salary') && (
            <div className="text-[#E7000B] font-sans text-body-03 font-normal leading-normal">{getError('salary')}</div>
          )}
        </div>
      </div>

      {/* Row 4: Severance select fields + upload + notes */}
      {showSeveranceSubSection && (
        <>
          <div className="grid grid-cols-2 gap-lg items-end">
            <div className="flex flex-col gap-sm">
              <Dropdown
                label="Did employee receive severance package relating to this employment?"
                placeholder="Choose an option"
                options={YES_NO_OPTIONS}
                value={(data.severancePackageReceived as string) || ''}
                onChange={(value) => {
                  onFieldChange('severancePackageReceived', value);
                  onClearFieldError('severancePackageReceived');
                  handleBlur('severancePackageReceived');
                }}
                error={getError('severancePackageReceived')}
                required
              />
              <div className="min-h-[20px]" />
            </div>

            <div className="flex flex-col gap-sm">
              <Dropdown
                label="Did the policyholder waive their right to a tax free lump sum from the occupational pension scheme?"
                placeholder="Choose an option"
                options={YES_NO_OPTIONS}
                value={(data.severanceWaivedLumpSum as string) || ''}
                onChange={(value) => {
                  onFieldChange('severanceWaivedLumpSum', value);
                  onClearFieldError('severanceWaivedLumpSum');
                  handleBlur('severanceWaivedLumpSum');
                }}
                error={getError('severanceWaivedLumpSum')}
                required
              />
              <div className="min-h-[20px]" />
            </div>
          </div>

          {/* Upload documents + Severance notes — only when severancePackageReceived is Yes */}
          {(data.severancePackageReceived as string) === 'Yes' && (
            <>
              <FileUploadField
                fieldKey="severanceDocumentUpload"
                label="Upload documents"
                files={pendingFiles?.get('severanceDocumentUpload')}
                onAddFile={onAddFile}
                onRemoveFile={onRemoveFile}
              />

              {/* Severance package notes */}
              <div className="flex flex-col gap-sm">
                <TextArea
                  label="Severance package notes"
                  placeholder="Severance package notes"
                  maxLength={2000}
                  value={(data.severanceNotes as string) || ''}
                  onChange={(e) => {
                    onFieldChange('severanceNotes', e.target.value);
                  }}
                  fluid
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
